import { formatDateFromISO } from '@practium/shared';
import {
	DatePicker,
	FormField,
	FormInput,
	FormSectionTitle,
	FormSelect,
	RowActionButton,
} from '@practium/ui';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { DOSE_UNIT_OPTIONS } from '../../../constants/foliar.constants';
import type { UseFoliarFormOptions } from '../../../hooks/useFoliarForm';
import { useFoliarForm } from '../../../hooks/useFoliarForm';
import type { FoliarApplicationAdvice, RecommendationPageMode } from '../../../types';
import { MultiSelect } from '../../MultiSelect/MultiSelect';
import styles from './FoliarForm.module.css';

export interface FoliarFormRef {
	submit: () => Promise<void>;
	isDirty: () => boolean;
}

export interface FoliarFormProps extends UseFoliarFormOptions {
	mode?: RecommendationPageMode;
	viewData?: FoliarApplicationAdvice;
	onLoadingChange?: (loading: boolean) => void;
	onPropertyChange?: (propertyId: string) => void;
}

const getName = (value: unknown): string => {
	if (!value) return '';

	if (typeof value === 'string') return value;

	if (typeof value === 'object' && 'name' in value) {
		return String((value as { name?: string }).name ?? '');
	}

	return '';
};

export const FoliarForm = forwardRef<FoliarFormRef, FoliarFormProps>(function FoliarForm(
	{ mode = 'add', viewData, onLoadingChange, onPropertyChange, ...hookProps },
	ref
) {
	const {
		form,
		errors,
		isLoading,
		computedResult,
		set,
		setProduct,
		addProduct,
		removeProduct,
		handleSubmit,
		isDirty,
		propOptions,
		areaOptions,
		sectorOptions,
		classProductOptions,
		getProductOptions,
	} = useFoliarForm({ ...hookProps });

	useImperativeHandle(ref, () => ({ submit: handleSubmit, isDirty }));

	useEffect(() => {
		onLoadingChange?.(isLoading);
	}, [isLoading, onLoadingChange]);

	useEffect(() => {
		onPropertyChange?.(form.prop);
	}, [form.prop, onPropertyChange]);

	const isView = mode === 'view';
	const activeSectorOptions = sectorOptions ?? (form.area ? (sectorOptions[form.area] ?? []) : []);

	if (isView && viewData) {
		const firstProduct = viewData.products?.[0];

		return (
			<div className={styles.form}>
				<div className={styles.section}>
					<FormSectionTitle>Identificação</FormSectionTitle>

					<div className={styles.sectionFields}>
						<div className={styles.row2}>
							<FormField label="Identificação">
								<span className={styles.viewText}>{viewData.name || '—'}</span>
							</FormField>

							<FormField label="Propriedade">
								<span className={styles.viewText}>
									{viewData.propName || getName(viewData.prop) || '—'}
								</span>
							</FormField>
						</div>

						<div className={styles.row2}>
							<FormField label="Área">
								<span className={styles.viewText}>
									{viewData.areaName || getName(viewData.area) || '—'}
								</span>
							</FormField>

							<FormField label="Setores">
								<span className={styles.viewText}>
									{viewData.sectors?.map(getName).filter(Boolean).join(', ') || '—'}
								</span>
							</FormField>
						</div>

						<div className={styles.row2}>
							<FormField label="Data da análise">
								<span className={styles.viewText}>
									{formatDateFromISO(viewData.applicationDate)}
								</span>
							</FormField>

							<FormField label="Relatório">
								{viewData.reportUrl ? (
									<a
										href={viewData.reportUrl}
										target="_blank"
										rel="noopener noreferrer"
										className={styles.reportLink}
										title={viewData.reportUrl}
									>
										Ver Relatório
									</a>
								) : (
									<span className={styles.viewText}>—</span>
								)}
							</FormField>
						</div>
					</div>
				</div>

				<div className={styles.section}>
					<FormSectionTitle>Aplicação</FormSectionTitle>

					<div className={styles.sectionFields}>
						<div className={styles.row3}>
							<FormField label="Vol. do tanque (L)">
								<span className={styles.viewText}>
									{viewData.tankVolume?.toLocaleString('pt-BR') ?? '—'}
								</span>
							</FormField>

							<FormField label="Vol. de calda (L/ha)">
								<span className={styles.viewText}>
									{viewData.syrupVolume?.toLocaleString('pt-BR') ?? '—'}
								</span>
							</FormField>

							<FormField label="Nº de tanques a preparar">
								<span className={styles.viewText}>{viewData.tanksToPrepare ?? '—'}</span>
							</FormField>
						</div>

						{viewData.products?.map((product, idx) => (
							<div key={`${getName(product.productCommercialName) || product.productClass}-${idx}`}>
								{idx > 0 && <div className={styles.productsSeparator} />}

								<div className={styles.productViewRow}>
									<FormField label="Classe do produto">
										<span className={styles.viewText}>{product.productClass || '—'}</span>
									</FormField>

									<FormField label="Nome comercial">
										<span className={styles.viewText}>
											{getName(product.productCommercialName) || '—'}
										</span>
									</FormField>

									<FormField label="Marca comercial">
										<span className={styles.viewText}>{product.productTradeMark || '—'}</span>
									</FormField>

									<FormField label={`Dose (${product.doseMeasurementUnit ?? '/ha'})`}>
										<span className={styles.viewText}>{product.dose ?? '—'}</span>
									</FormField>
								</div>
							</div>
						))}

						<div className={styles.row2Result}>
							<FormField label="Qtd. por tanque">
								<span className={styles.viewText}>
									{firstProduct?.quantityPerTank?.toFixed(2) ?? '—'}
								</span>
							</FormField>

							<FormField label="Total necessário">
								<span className={styles.viewText}>
									{firstProduct?.totalNecessary?.toFixed(2) ?? '—'}
								</span>
							</FormField>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className={styles.form}>
			<div className={styles.section}>
				<FormSectionTitle>Identificação</FormSectionTitle>

				<div className={styles.sectionFields}>
					<div className={styles.row2}>
						<FormField label="Identificação" required error={errors.name}>
							<FormInput
								placeholder="Ex.: Recomendação João - 2025/2026"
								value={form.name}
								onChange={(e) => set('name', e.target.value)}
								hasError={!!errors.name}
							/>
						</FormField>

						<FormField label="Propriedade" required error={errors.prop}>
							<FormSelect
								value={form.prop}
								onChange={(v) => set('prop', v)}
								options={propOptions}
								placeholder="Selecione a propriedade"
								hasError={!!errors.prop}
							/>
						</FormField>
					</div>

					<div className={styles.row2}>
						<FormField label="Área" required error={errors.area}>
							<FormSelect
								value={form.area}
								onChange={(v) => {
									set('area', v);
									set('sectors', []);
								}}
								options={areaOptions}
								placeholder="Selecione a área"
								hasError={!!errors.area}
							/>
						</FormField>

						<FormField label="Setores" required error={errors.sectors}>
							<MultiSelect
								values={form.sectors}
								onChange={(v) => set('sectors', v)}
								options={activeSectorOptions}
								placeholder="Selecione os setores"
								hasError={!!errors.sectors}
								disabled={!form.area}
							/>
						</FormField>
					</div>

					<div className={styles.halfLeft}>
						<div className={styles.halfLeftField}>
							<FormField label="Data da análise" required error={errors.applicationDate}>
								<DatePicker
									value={form.applicationDate}
									onChange={(v) => set('applicationDate', v)}
									placeholder="Ex.: xx/xx/xxxx"
									hasError={!!errors.applicationDate}
								/>
							</FormField>
						</div>
					</div>
				</div>
			</div>

			<div className={styles.section}>
				<FormSectionTitle>Aplicação</FormSectionTitle>

				<div className={styles.sectionFields}>
					<div className={styles.row3}>
						<FormField label="Vol. do tanque (L)" required error={errors.tankVolume}>
							<FormInput
								placeholder="Ex.: 50000"
								type="number"
								value={form.tankVolume}
								onChange={(e) => set('tankVolume', e.target.value)}
								hasError={!!errors.tankVolume}
							/>
						</FormField>

						<FormField label="Vol. de calda (L/ha)" required error={errors.syrupVolume}>
							<FormInput
								placeholder="Ex.: 7000"
								type="number"
								value={form.syrupVolume}
								onChange={(e) => set('syrupVolume', e.target.value)}
								hasError={!!errors.syrupVolume}
							/>
						</FormField>

						<FormField label="Nº de tanques a preparar" required error={errors.tanksToPrepare}>
							<FormInput
								placeholder="Ex.: 3"
								type="number"
								value={form.tanksToPrepare}
								onChange={(e) => set('tanksToPrepare', e.target.value)}
								hasError={!!errors.tanksToPrepare}
							/>
						</FormField>
					</div>

					{form.products.map((product, idx) => {
						const isFirst = idx === 0;
						const productKey = `product-${idx}-${product.productClass}`;

						const { productCommercialNameOptions, productTradeMarkOptions } =
							getProductOptions(product);

						return (
							<div key={productKey} className={styles.productWrapper}>
								<div className={styles.fieldClasse}>
									<FormField
										label="Classe do produto"
										required={isFirst}
										error={
											errors[`products.${idx}.productClass`] ??
											(isFirst ? errors.products : undefined)
										}
									>
										<FormSelect
											value={product.productClass}
											onChange={(v) => {
												setProduct(idx, 'productClass', v);
												setProduct(idx, 'productCommercialName', '');
												setProduct(idx, 'productTradeMark', '');
											}}
											options={classProductOptions}
											placeholder="Selecione"
										/>
									</FormField>
								</div>

								<div className={styles.fieldNome}>
									<FormField
										label="Nome comercial"
										required={isFirst}
										error={errors[`products.${idx}.productCommercialName`]}
									>
										<FormSelect
											value={product.productCommercialName}
											onChange={(v) => {
												setProduct(idx, 'productCommercialName', v);
												setProduct(idx, 'productTradeMark', '');
											}}
											options={productCommercialNameOptions}
											placeholder="Selecione"
										/>
									</FormField>
								</div>

								<div className={styles.fieldMarca}>
									<FormField label="Marca comercial" error={undefined}>
										<FormSelect
											clearable
											value={product.productTradeMark}
											onChange={(v) => setProduct(idx, 'productTradeMark', v)}
											options={productTradeMarkOptions}
											placeholder="Selecione"
										/>
									</FormField>
								</div>

								<div className={styles.fieldDose}>
									<FormField
										label="Dose (kg)"
										required={isFirst}
										error={errors[`products.${idx}.dose`]}
									>
										<FormInput
											placeholder="Ex.: 20.5"
											type="number"
											value={product.dose}
											onChange={(e) => setProduct(idx, 'dose', e.target.value)}
										/>
									</FormField>
								</div>

								<div className={styles.fieldUnit}>
									<FormField
										label="Unidade da dose"
										error={errors[`products.${idx}.doseMeasurementUnit`]}
									>
										<FormSelect
											clearable
											hasError={!!errors[`products.${idx}.doseMeasurementUnit`]}
											value={product.doseMeasurementUnit}
											onChange={(v) =>
												setProduct(idx, 'doseMeasurementUnit', v as '/ha' | '/100L' | '')
											}
											options={DOSE_UNIT_OPTIONS}
											placeholder="Selecione"
										/>
									</FormField>
								</div>

								<div className={styles.fieldActions}>
									{isFirst && <RowActionButton variant="add" onClick={addProduct} />}
									<RowActionButton variant="delete" onClick={() => removeProduct(idx)} />
								</div>
							</div>
						);
					})}

					<div className={styles.row2Result}>
						<FormField label="Resultado: Qtd. por tanque (kg)">
							<FormInput
								value={computedResult ? computedResult.quantityPerTank.toFixed(2) : '0.00'}
								disabled
								className={styles.readonlyInput}
							/>
						</FormField>

						<FormField label="Resultado: Total necessário (kg)">
							<FormInput
								value={computedResult ? computedResult.totalNecessary.toFixed(2) : '0.00'}
								disabled
								className={styles.readonlyInput}
							/>
						</FormField>
					</div>
				</div>
			</div>
		</div>
	);
});
